import React from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ShieldAlert, ArrowLeft } from 'lucide-react';
import { useAuthStore } from '@/stores/auth.store';
import type { UserRole } from '@/types';
import { decodeJwtRole, roleMatchesAllowlist } from '@/lib/auth-role';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';

interface ProtectedRouteProps {
  roles?: UserRole[];
  requiredRoles?: UserRole[];
  children?: React.ReactNode;
  redirectTo?: string;
}

export function ProtectedRoute({ roles, requiredRoles, children, redirectTo = '/login' }: ProtectedRouteProps) {
  const { t } = useTranslation();
  const { isAuthenticated, user, token, isLoading } = useAuthStore();
  const location = useLocation();
  const effectiveRoles = requiredRoles || roles;

  const accessToken = new URLSearchParams(location.search).get('accessToken');
  const isLearnerPlayWithToken = Boolean(
    accessToken && /^\/eduflow\/flows\/[^/]+\/play$/.test(location.pathname),
  );

  if (!isAuthenticated && !isLearnerPlayWithToken) {
    const returnTo = `${location.pathname}${location.search}`;
    const loginTarget =
      redirectTo === '/login'
        ? `/login?returnTo=${encodeURIComponent(returnTo)}`
        : redirectTo;
    return <Navigate to={loginTarget} state={{ from: location }} replace />;
  }

  if (effectiveRoles && effectiveRoles.length > 0) {
    const lsToken = typeof window !== 'undefined' ? localStorage.getItem('auth_token') : null;
    const activeToken = token ?? lsToken;
    const fromUser = user?.role != null ? String(user.role) : null;
    const fromJwt = decodeJwtRole(activeToken);
    const resolvedRole = fromUser ?? fromJwt;

    if (!resolvedRole) {
      if (isLoading) {
        return <LoadingSpinner fullScreen />;
      }
      return <Navigate to={redirectTo} state={{ from: location }} replace />;
    }

    if (!roleMatchesAllowlist(resolvedRole, effectiveRoles as string[])) {
      const displayRole = fromUser ?? fromJwt ?? resolvedRole;
      return (
        <div className="flex min-h-screen items-center justify-center bg-gray-50 dark:bg-gray-900 px-4">
          <div className="w-full max-w-md text-center space-y-6">
            <div className="flex justify-center">
              <div className="flex h-20 w-20 items-center justify-center rounded-full bg-red-100 dark:bg-red-900/30">
                <ShieldAlert className="h-10 w-10 text-red-600 dark:text-red-400" />
              </div>
            </div>

            <div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                {t('auth.accessDenied', 'Access Denied')}
              </h1>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
                {t(
                  'auth.accessDeniedDesc',
                  "You don't have permission to access this page. Contact your administrator if you believe this is an error.",
                )}
              </p>
            </div>

            <div className="flex flex-col items-center gap-3">
              <button
                type="button"
                onClick={() => window.history.back()}
                className="inline-flex items-center gap-2 rounded-lg bg-gradient-to-r from-primary-600 to-primary-700 px-6 py-2.5 text-sm font-medium text-white shadow-sm hover:from-primary-700 hover:to-primary-800 transition-all"
              >
                <ArrowLeft className="h-4 w-4" />
                {t('auth.goBack', 'Go back')}
              </button>
              <p className="text-xs text-gray-400 dark:text-gray-500">
                {t('auth.currentRole', 'Your role: {{role}}', { role: displayRole })}
              </p>
            </div>
          </div>
        </div>
      );
    }
  }

  return children ? <>{children}</> : <Outlet />;
}

export default ProtectedRoute;
